import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';

export { default as NeighborhoodGridSection } from './Section.vue';

export interface LinkGroup {
	title: string;
	items: { label: string; href: string }[];
}

interface NeighborhoodLink {
	displayName: string;
}

interface PropertyTypeConfig {
	titlePlural: string;
	type: PropertyTypeEnum;
	neighborhoods: NeighborhoodLink[];
}

interface CityConfig {
	name: string;
	uf: string;
	macroregion: string;
}

const PROPERTY_TYPE_LABEL_MAP: Partial<Record<PropertyTypeEnum, string>> = {
	[PropertyTypeEnum.APARTMENT]: 'Apartamentos',
	[PropertyTypeEnum.HOUSE]: 'Casas',
	[PropertyTypeEnum.PENTHOUSE]: 'Coberturas',
	[PropertyTypeEnum.CONDO_HOUSE]: 'Casas de condomínio'
};

function buildPropertyUrl(
	neighborhood: NeighborhoodLink,
	propertyType: PropertyTypeEnum,
	city: CityConfig
): string {
	return PropertyFiltersPresenter.getPathFromFilters({
		transactionType: 'sell',
		states: [city.uf],
		cities: [city.name],
		regions: [neighborhood.displayName],
		propertyTypes: [propertyType],
		macroregions: [city.macroregion]
	});
}

function buildLabel(
	propertyType: PropertyTypeEnum,
	neighborhoodName: string
): string {
	const propertyTypeLabel = PROPERTY_TYPE_LABEL_MAP[propertyType] || '';
	return `${propertyTypeLabel} em ${neighborhoodName}`;
}

function createLinkGroup(
	propertyTypeConfig: PropertyTypeConfig,
	city: CityConfig
): LinkGroup {
	return {
		title: `${propertyTypeConfig.titlePlural} por bairro`,
		items: propertyTypeConfig.neighborhoods.map(neighborhood => ({
			label: buildLabel(propertyTypeConfig.type, neighborhood.displayName),
			href: buildPropertyUrl(neighborhood, propertyTypeConfig.type, city)
		}))
	};
}

// São Paulo configuration
const saoPauloCity: CityConfig = {
	name: 'São Paulo',
	uf: 'SP',
	macroregion: 'São Paulo'
};

const saoPauloPropertyTypes: PropertyTypeConfig[] = [
	{
		titlePlural: 'Apartamentos',
		type: PropertyTypeEnum.APARTMENT,
		neighborhoods: [
			{ displayName: 'Moema' },
			{ displayName: 'Cidade Jardim' },
			{ displayName: 'Perdizes' },
			{ displayName: 'Vila Mariana' },
			{ displayName: 'Higienópolis' }
		]
	},
	{
		titlePlural: 'Casas',
		type: PropertyTypeEnum.HOUSE,
		neighborhoods: [
			{ displayName: 'Morumbi' },
			{ displayName: 'Cidade Jardim' },
			{ displayName: 'Alto da Boa Vista' },
			{ displayName: 'Vila Mariana' },
			{ displayName: 'Vila Madalena' }
		]
	},
	{
		titlePlural: 'Coberturas',
		type: PropertyTypeEnum.PENTHOUSE,
		neighborhoods: [
			{ displayName: 'Vila Madalena' },
			{ displayName: 'Vila Olímpia' },
			{ displayName: 'Vila Mariana' },
			{ displayName: 'Alto da Boa Vista' },
			{ displayName: 'Moema' }
		]
	},
	{
		titlePlural: 'Casas de condomínio',
		type: PropertyTypeEnum.CONDO_HOUSE,
		neighborhoods: [
			{ displayName: 'Alto da Boa Vista' },
			{ displayName: 'Morumbi' },
			{ displayName: 'Jardim Guedala' },
			{ displayName: 'Brooklin' },
			{ displayName: 'Campo Belo' }
		]
	}
];

// Curitiba configuration
const curitibaCity: CityConfig = {
	name: 'Curitiba',
	uf: 'PR',
	macroregion: 'Curitiba'
};

const curitibaPropertyTypes: PropertyTypeConfig[] = [
	{
		titlePlural: 'Apartamentos',
		type: PropertyTypeEnum.APARTMENT,
		neighborhoods: [
			{ displayName: 'Batel' },
			{ displayName: 'Água Verde' },
			{ displayName: 'Ecoville' },
			{ displayName: 'Bigorrilho' },
			{ displayName: 'Mossunguê' }
		]
	},
	{
		titlePlural: 'Casas de condomínio',
		type: PropertyTypeEnum.CONDO_HOUSE,
		neighborhoods: [
			{ displayName: 'Campo Comprido' },
			{ displayName: 'Santa Felicidade' },
			{ displayName: 'São Braz' },
			{ displayName: 'Uberaba' },
			{ displayName: 'Santa Cândida' }
		]
	},
	{
		titlePlural: 'Casas',
		type: PropertyTypeEnum.HOUSE,
		neighborhoods: [
			{ displayName: 'Santa Felicidade' },
			{ displayName: 'Ecoville' },
			{ displayName: 'Centro' },
			{ displayName: 'Vista Alegre' },
			{ displayName: 'Mercês' }
		]
	},
	{
		titlePlural: 'Coberturas',
		type: PropertyTypeEnum.PENTHOUSE,
		neighborhoods: [
			{ displayName: 'Água Verde' },
			{ displayName: 'Batel' },
			{ displayName: 'Bigorrilho' },
			{ displayName: 'Ecoville' },
			{ displayName: 'Juvevê' }
		]
	}
];

// Generate link groups
export const saoPauloGroups: LinkGroup[] = saoPauloPropertyTypes.map(
	propertyType => createLinkGroup(propertyType, saoPauloCity)
);

export const curitibaGroups: LinkGroup[] = curitibaPropertyTypes.map(
	propertyType => createLinkGroup(propertyType, curitibaCity)
);
